import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

const ProductsSection = () => {
  const navigate = useNavigate();
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return (
    <section className="relative w-full bg-white">
      {/* Desktop Version */}
      {!isMobile && (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24">
          <div className="grid lg:grid-cols-2 gap-16">
            {/* Left Content */}
            <div>
              <h1 className="text-5xl lg:text-6xl font-bold text-gray-900 mb-6">
                GROWING BETTER
                <span className="block text-[#1770b6]">AND FASTER</span>
              </h1>
              <p className="text-gray-600 text-lg leading-relaxed mb-8">
                We produce feed for a complete range of fish and crustaceans consumed by both the local Philippine market and the export market. The consistent quality and performance of Lion Head Aquafeed is reflected on the quality of life enjoyed by our customers.
              </p>
              
              <div className="space-y-6">
                {/* Bullet Point 1 */}
                <div className="flex gap-3">
                  <div className="flex-shrink-0 w-6 h-6 rounded-full bg-[#1770b6] flex items-center justify-center mt-1">
                    <span className="text-white text-xs">✓</span>
                  </div>
                  <div>
                    <p className="text-gray-800 font-medium">
                      Consistent quality, high performance feed produced in world class, ISO certified manufacturing facilities
                    </p>
                    <p className="text-gray-500 text-sm mt-1">
                      Optimally bioavailable ingredients for fast growth, improved FCR, less waste, and a better environment
                    </p>
                  </div>
                </div>
                
                {/* Bullet Point 2 */}
                <div className="flex gap-3">
                  <div className="flex-shrink-0 w-6 h-6 rounded-full bg-[#1770b6] flex items-center justify-center mt-1">
                    <span className="text-white text-xs">✓</span>
                  </div>
                  <div>
                    <p className="text-gray-800 font-medium">
                      Sustainable and healthy produce brought about by Probiotics incorporated in the feed
                    </p>
                    <p className="text-gray-500 text-sm mt-1">
                      Enhanced feed palatability and attractiveness that lead to better weight gains
                    </p>
                  </div>
                </div>
              </div>
              
              <button 
                onClick={() => navigate('/fish-food')}
                className="mt-10 inline-flex items-center gap-2 bg-[#1770b6] text-white px-8 py-3 rounded-full font-semibold hover:bg-[#0e5a94] transition-colors"
              >
                PRODUCTS <span className="text-lg">→</span>
              </button>
            </div>
            
            {/* Right Column - Image or Stats */}
            <div className="bg-gray-50 rounded-2xl p-8 flex items-center justify-center">
             <img src='https://chennaiaquarium.in/storage/product/P-1116/hr5OkTaBdrM2qsLOZdvAhFhVrmUitaGLqYlf19wu.jpg' alt='logo'/>
            </div>
          </div>
        </div>
      )}

      {/* Mobile Version - App-like */}
      {isMobile && (
        <div className="px-4 py-12">
          <div className="space-y-8">
            {/* Header */}
            <div className="text-center">
              <h1 className="text-3xl font-bold text-gray-900 mb-3">
                GROWING BETTER
                <span className="block text-[#1770b6]">AND FASTER</span>
              </h1>
              <p className="text-gray-600 text-sm leading-relaxed">
                We produce feed for a complete range of fish and crustaceans consumed by both the local Philippine market and the export market.
              </p>
            </div>

            {/* Feature Cards */}
            <div className="space-y-4">
              <div className="bg-gray-50 rounded-xl p-5">
                <div className="flex items-start gap-3">
                  <div className="w-5 h-5 rounded-full bg-[#1770b6] flex items-center justify-center mt-0.5 flex-shrink-0">
                    <span className="text-white text-[10px]">✓</span>
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-gray-800 mb-2">
                      Consistent quality, high performance feed
                    </p>
                    <p className="text-xs text-gray-500">
                      Produced in world class, ISO certified manufacturing facilities
                    </p>
                    <p className="text-xs text-gray-500 mt-2">
                      Optimally bioavailable ingredients for fast growth, improved FCR, less waste, and a better environment
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-gray-50 rounded-xl p-5">
                <div className="flex items-start gap-3">
                  <div className="w-5 h-5 rounded-full bg-[#1770b6] flex items-center justify-center mt-0.5 flex-shrink-0">
                    <span className="text-white text-[10px]">✓</span>
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-gray-800 mb-2">
                      Sustainable and healthy produce
                    </p>
                    <p className="text-xs text-gray-500">
                      Probiotics incorporated in the feed
                    </p>
                    <p className="text-xs text-gray-500 mt-2">
                      Enhanced feed palatability and attractiveness that lead to better weight gains
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Stats Cards */}
            <div className="grid grid-cols-2 gap-3">
              <div className="bg-gradient-to-br from-[#1770b6]/5 to-white rounded-xl p-3 text-center border border-[#1770b6]/10">
                <div className="text-[#1770b6] text-2xl font-bold">10+</div>
                <div className="text-[10px] text-gray-500">Years Excellence</div>
              </div>
              <div className="bg-gradient-to-br from-[#1770b6]/5 to-white rounded-xl p-3 text-center border border-[#1770b6]/10">
                <div className="text-[#1770b6] text-2xl font-bold">50+</div>
                <div className="text-[10px] text-gray-500">Fish Species</div>
              </div>
              <div className="bg-gradient-to-br from-[#1770b6]/5 to-white rounded-xl p-3 text-center border border-[#1770b6]/10">
                <div className="text-[#1770b6] text-2xl font-bold">ISO</div>
                <div className="text-[10px] text-gray-500">Certified</div>
              </div>
              <div className="bg-gradient-to-br from-[#1770b6]/5 to-white rounded-xl p-3 text-center border border-[#1770b6]/10">
                <div className="text-[#1770b6] text-2xl font-bold">100%</div>
                <div className="text-[10px] text-gray-500">Natural</div>
              </div>
            </div>

            {/* Products Button */}
            <button 
              onClick={() => navigate('/fish-food')}
              className="w-full bg-[#1770b6] text-white py-4 rounded-full font-semibold flex items-center justify-center gap-2 active:scale-95 transition-transform"
            >
              PRODUCTS <span>→</span>
            </button>
          </div>
        </div>
      )}
    </section>
  );
};

export default ProductsSection;